import React, { useState, useEffect } from "react";
import { cn } from "../../Lib/Cn";
import { Button } from "../UI/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../UI/select";

const PaginationV2 = ({
    currentPage = 1,
    totalPages = 1,
    rowsPerPage = 10,
    onRowsPerPageChange,
    onPageChange,
    pageSizeOptions = [10, 25, 50, 100],
    className = "",
    disabled = false,
}) => {
    const [inputPage, setInputPage] = useState(currentPage);

    useEffect(() => {
        setInputPage(currentPage);
    }, [currentPage]);

    const handleCommit = () => {
        const parsed = parseInt(inputPage, 10);
        const targetPage = isNaN(parsed) ? 1 : Math.max(1, Math.min(parsed, totalPages));
        setInputPage(targetPage);
        if (currentPage !== targetPage) onPageChange?.(targetPage);
    };

    const getPageNumbers = () => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
        if (currentPage >= totalPages - 3) {
            return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }
        return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
    };

    return (
        <div className={cn("hidden lg:flex justify-between items-center mt-3 select-none", className)}>
            {/* Left: Page Input & Rows per Page */}
            <div className="flex items-center gap-3 text-sm text-[#2e2e2e]">
                <div className="flex items-center gap-1.5">
                    <span className="text-sub-text-color">Page</span>
                    <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={inputPage}
                        disabled={disabled}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") {
                                setInputPage("");
                                return;
                            }
                            const num = parseInt(val, 10);
                            if (isNaN(num)) return;
                            const maxLimit = Math.max(1, totalPages);
                            if (num > maxLimit) {
                                setInputPage(maxLimit);
                            } else if (num < 1) {
                                setInputPage(1);
                            } else {
                                setInputPage(num);
                            }
                        }}
                        onBlur={handleCommit}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                            if (e.key === "Escape") {
                                setInputPage(currentPage);
                                e.currentTarget.blur();
                            }
                        }}
                        className="w-11 h-8 text-center text-sm font-medium bg-white sahi-radius border filter-border text-gray-800 focus:border-[#757575] focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
                    />
                    <span className="text-sub-text-color">of {Math.max(1, totalPages)}</span>
                </div>

                <div className="h-4 w-px bg-[#d1d5db]" />

                {onRowsPerPageChange && (
                    <div className="flex items-center gap-2">
                        <span className="text-sub-text-color whitespace-nowrap">Rows per page</span>
                        <Select
                            value={String(rowsPerPage)}
                            disabled={disabled}
                            onValueChange={(val) => {
                                onRowsPerPageChange(Number(val));
                                onPageChange?.(1);
                            }}
                        >
                            <SelectTrigger className="h-8 w-auto min-w-[58px] px-2.5 py-1 text-sm bg-white sahi-radius border filter-border font-medium hover:border-[#757575] focus:border-[#757575] focus:ring-0 gap-1.5 [&>svg]:ml-1">
                                <SelectValue placeholder={rowsPerPage} />
                            </SelectTrigger>
                            <SelectContent className="bg-white min-w-[70px]">
                                {pageSizeOptions.map((size) => (
                                    <SelectItem key={size} value={String(size)} className="cursor-pointer text-sm">
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            {/* Right: Navigation & Numbered Pages */}
            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="pgn"
                    disabled={disabled || currentPage <= 1}
                    onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                    className="h-8 w-8 p-0 bg-transparent border-0 hover:bg-gray-200/80 active:bg-gray-300 text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                    title="Previous Page"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center gap-1 mx-1">
                    {getPageNumbers().map((page, idx) =>
                        page === "..." ? (
                            <span key={`ellipsis-${idx}`} className="min-w-[20px] h-8 px-1 flex items-center justify-center text-sm text-gray-500 select-none">
                                ...
                            </span>
                        ) : (
                            <button
                                key={`page-${page}`}
                                type="button"
                                disabled={disabled}
                                onClick={() => onPageChange?.(page)}
                                className={cn(
                                    "min-w-[32px] h-8 px-2 text-sm sahi-radius transition-colors flex items-center justify-center cursor-pointer",
                                    page === currentPage
                                        ? "bg-white border border-[#757575] font-bold text-gray-900 shadow-xs"
                                        : "bg-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-200/80 active:bg-gray-300 font-normal"
                                )}
                            >
                                {page}
                            </button>
                        )
                    )}
                </div>

                <Button
                    variant="ghost"
                    size="pgn"
                    disabled={disabled || currentPage >= totalPages}
                    onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
                    className="h-8 w-8 p-0 bg-transparent border-0 hover:bg-gray-200/80 active:bg-gray-300 text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                    title="Next Page"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
};

export default PaginationV2;
